import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useStore } from '../store/useStore';
import { getCountryFromIP, getDefaultCurrency } from '../services/geolocationService';
import { CURRENCIES } from '../constants';
import { useCurrency } from '../hooks/useCurrency';
import '../styles/Products.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Products() {
  const [searchParams] = useSearchParams();
  const products = useStore((state) => state.products);
  const setSelectedCurrency = useStore((state) => state.setSelectedCurrency);
  const { convertPrice, currencySymbol, selectedCurrency } = useCurrency();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sortBy] = useState('newest');
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [categories, setCategories] = useState(['Mens', 'Womens', 'Kids', 'Sports']);
  const [catalogMinPrice, setCatalogMinPrice] = useState(0);
  const [catalogMaxPrice, setCatalogMaxPrice] = useState(500);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(500);

  // Scroll to top on component mount or when search params change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [searchParams]);

  // Detect country and set default currency
  useEffect(() => {
    const detectCurrency = async () => {
      const countryCode = await getCountryFromIP();
      const currency = getDefaultCurrency(countryCode);
      setSelectedCurrency(currency);
    };
    detectCurrency();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load categories from API and poll for updates
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories`);
        if (response.ok) {
          const data = await response.json();
          // Extract just the names from the category objects
          const categoryNames = data.map(cat => cat.name);
          if (Array.isArray(categoryNames) && categoryNames.length > 0) {
            setCategories(categoryNames);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fall back to defaults if API fails
        setCategories(['Mens', 'Womens', 'Kids', 'Sports']);
      }
    };

    // Fetch on mount
    fetchCategories();
  }, []);

  // Helper function to round down to whole fifths
  const roundDownToFifth = useCallback((price) => {
    return Math.floor(price / 5) * 5;
  }, []);

  // Wrap convertPrice to include rounding logic
  const convertPriceRounded = useCallback((priceUSD) => {
    const converted = parseFloat(convertPrice(priceUSD));
    return roundDownToFifth(converted).toFixed(2);
  }, [convertPrice, roundDownToFifth]);

  // Get unique colors from products
  const availableColors = useMemo(() => {
    const colorMap = new Map();
    products.forEach(product => {
      if (product.colors) {
        product.colors.forEach(color => {
          // Use hex as unique key since color.id might not be unique
          const colorKey = color.color_hex || color.id;
          if (!colorMap.has(colorKey)) {
            colorMap.set(colorKey, { id: color.id, name: color.color_name, hex: color.color_hex });
          }
        });
      }
    });
    return Array.from(colorMap.values());
  }, [products]);

  // Get unique sizes from products
  const availableSizes = useMemo(() => {
    const sizeSet = new Set();
    products.forEach(product => {
      // Extract sizes from stockByColor: { [colorId]: { [size]: quantity } }
      if (product.stockByColor) {
        Object.values(product.stockByColor).forEach(colorStock => {
          if (colorStock && typeof colorStock === 'object') {
            Object.keys(colorStock).forEach(size => {
              sizeSet.add(size);
            });
          }
        });
      }
    });

    // Sort sizes in ascending order
    const sortedSizes = Array.from(sizeSet).sort((a, b) => parseFloat(a) - parseFloat(b));

    // Reorder for column-first display (top-to-bottom, then next column)
    const reorderForColumns = (arr, numColumns = 2) => {
      const itemsPerColumn = Math.ceil(arr.length / numColumns);
      const result = [];

      for (let i = 0; i < itemsPerColumn; i++) {
        for (let col = 0; col < numColumns; col++) {
          const index = col * itemsPerColumn + i;
          if (index < arr.length) {
            result.push(arr[index]);
          }
        }
      }

      return result;
    };

    return reorderForColumns(sortedSizes);
  }, [products]);

  // Calculate min and max prices from catalog
  useEffect(() => {
    if (products.length > 0) {
      const prices = products.map(p => parseFloat(p.price));
      const minPrice = Math.floor(Math.min(...prices));
      const maxPrice = Math.ceil(Math.max(...prices));
      setCatalogMinPrice(minPrice);
      setCatalogMaxPrice(maxPrice);
      setMinPrice(minPrice);
      setMaxPrice(maxPrice);
    }
  }, [products]);

  useEffect(() => {
    const category = searchParams.get('category') || 'all';
    setSelectedCategory(category);
  }, [searchParams]);

  useEffect(() => {
    let filtered = products;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        (product) => product.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filter by price range
    filtered = filtered.filter(
      (product) => parseFloat(product.price) >= minPrice && parseFloat(product.price) <= maxPrice
    );

    // Filter by colors
    if (selectedColors.length > 0) {
      filtered = filtered.filter((product) => {
        if (!product.colors) return false;
        return selectedColors.some(colorId =>
          product.colors.some(c => c.id === colorId)
        );
      });
    }

    // Filter by sizes
    if (selectedSizes.length > 0) {
      filtered = filtered.filter((product) => {
        if (!product.stockByColor) return false;
        // Check if any of the selected sizes exist in any color for this product
        return selectedSizes.some(size => {
          return Object.values(product.stockByColor).some(colorStock => {
            return colorStock && typeof colorStock === 'object' && size in colorStock;
          });
        });
      });
    }

    // Sort
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, minPrice, maxPrice, selectedColors, selectedSizes, sortBy, selectedCurrency]);

  const toggleColor = (colorId) => {
    setSelectedColors(prev =>
      prev.includes(colorId)
        ? prev.filter(id => id !== colorId)
        : [...prev, colorId]
    );
  };

  const toggleSize = (size) => {
    setSelectedSizes(prev =>
      prev.includes(size)
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  return (
    <div className="products-page">
      {/* Filter Panel - Slides from Left with All Filters */}
      {expandedFilter && (
        <>
          <div
            className="filter-panel-overlay"
            onClick={() => setExpandedFilter(null)}
          />
          <div className="filter-panel">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button
                className="filter-panel-close"
                onClick={() => setExpandedFilter(null)}
              >
                ×
              </button>
            </div>

            <div className="filter-panel-content">
              {/* Categories Section */}
              <div className="filter-section">
                <h4>Categories</h4>
                <div className="filter-grid">
                  <button
                    key="all"
                    className={`filter-grid-item ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedCategory('all')}
                  >
                    All
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.toLowerCase()}
                      className={`filter-grid-item ${selectedCategory === cat.toLowerCase() ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat.toLowerCase())}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Section */}
              <div className="filter-section">
                <h4>Price Range</h4>
                <div className="price-filter">
                  <div className="price-display">
                    <span>{currencySymbol}{convertPriceRounded(minPrice)}</span>
                    <span>−</span>
                    <span>{currencySymbol}{convertPriceRounded(maxPrice)}</span>
                  </div>
                  <div className="slider-container">
                    <input
                      type="range"
                      min={catalogMinPrice}
                      max={catalogMaxPrice}
                      value={minPrice}
                      onChange={(e) => {
                        const val = Math.min(parseInt(e.target.value) || catalogMinPrice, maxPrice - 1);
                        setMinPrice(val);
                      }}
                      className="slider min-slider"
                    />
                    <input
                      type="range"
                      min={catalogMinPrice}
                      max={catalogMaxPrice}
                      value={maxPrice}
                      onChange={(e) => {
                        const val = Math.max(parseInt(e.target.value) || catalogMaxPrice, minPrice + 1);
                        setMaxPrice(val);
                      }}
                      className="slider max-slider"
                    />
                  </div>
                </div>
              </div>

              {/* Color Section */}
              {availableColors.length > 0 && (
                <div className="filter-section">
                  <h4>Color</h4>
                  <div className="filter-grid color-grid">
                    {availableColors.map((color) => (
                      <button
                        key={color.id}
                        className={`filter-grid-item color-item ${selectedColors.includes(color.id) ? 'active' : ''}`}
                        onClick={() => toggleColor(color.id)}
                        title={color.name}
                      >
                        <span
                          className="color-swatch"
                          style={{ backgroundColor: color.hex }}
                        />
                        <span>{color.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size Section */}
              <div className="filter-section">
                <h4>Size (EU)</h4>
                <div className="filter-grid">
                  {availableSizes.map((size) => (
                    <button
                      key={size}
                      className={`filter-grid-item ${selectedSizes.includes(size) ? 'active' : ''}`}
                      onClick={() => toggleSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="products-container">
        {/* Main Content */}
        <div className="products-main">
          {/* Header */}
          <div className="products-header">
            <div className="header-controls">
              {/* Filter Button - Left */}
              <button
                className={`filter-btn ${expandedFilter ? 'active' : ''}`}
                onClick={() => setExpandedFilter(expandedFilter ? null : 'open')}
              >
                Filter By
              </button>

              {/* Currency Selector - Center */}
              <div className="currency-controls">
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="currency-select"
                  title="Select currency"
                >
                  {Object.entries(CURRENCIES).map(([code, { symbol, name }]) => (
                    <option key={code} value={code}>
                      {symbol} {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort By - Right
              <div className="sort-controls">
                <label>
                  Sort by:
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A to Z</option>
                  </select>
                </label>
              </div> */}
            </div>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? (
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  selectedCurrency={selectedCurrency}
                  convertPrice={convertPriceRounded}
                  currencySymbol={CURRENCIES[selectedCurrency].symbol}
                />
              ))}
            </div>
          ) : (
            <div className="no-products-message">
              <p>No products found. Try adjusting your filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
